"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.customFilter = exports.logic = exports.cascading = exports.bottomRanking = exports.topRanking = exports.measureBetweenNotEqual = exports.measureBetween = exports.measureLessThan = exports.measureLessThanOrEqual = exports.measureGreaterThanOrEqual = exports.measureGreaterThan = exports.measureEquals = exports.measureBase = exports.today = exports.thisQuarter = exports.thisMonth = exports.thisYear = exports.dateRelativeTo = exports.dateRelativeFrom = exports.dateRelative = exports.dateRange = exports.dateTo = exports.dateFrom = exports.members = exports.numeric = exports.betweenNotEqual = exports.between = exports.lessThanOrEqual = exports.lessThan = exports.greaterThanOrEqual = exports.greaterThan = exports.equals = exports.doesntEqual = exports.like = exports.startsWith = exports.endsWith = exports.contains = exports.doesntStartWith = exports.doesntEndWith = exports.doesntContain = exports.exclude = exports.intersection = exports.union = void 0;
const filters_js_1 = require("./filters.js");
// LOGICAL FILTERS
/**
 * Creates a filter representing the union of multiple filters on the same attribute. The resulting
 * union filter filters on items that match any of the given filters.
 *
 * To create 'or' filters using different attributes, use the {@link logic.and | `or()`} function.
 *
 * @example
 * Filter for countries that start with the letter 'A' **or** end with the letter 'A'
 * in the Sample ECommerce data model.
 * ```ts
 * filterFactory.union([
 *   filterFactory.startsWith(DM.Country.Country, 'A'),
 *   filterFactory.endsWith(DM.Country.Country, 'A'),
 * ])
 * ```
 * @param filters - Filters to union. The filters must all be on the same attribute.
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function union(filters, config) {
    return new filters_js_1.LogicalAttributeFilter(filters, filters_js_1.LogicalOperators.Union, config);
}
exports.union = union;
/**
 * Creates a filter representing the intersection of multiple filters on the same attribute. The resulting
 * intersection filter filters on items that match all of the given filters.
 *
 * To create 'and' filters using different attributes, use the {@link logic.and | `and()`} function.
 *
 * @example
 * Filter for countries that start with the letter 'A' **and** end with the letter 'A'
 * in the Sample ECommerce data model.
 * ```ts
 * filterFactory.intersection([
 *   filterFactory.startsWith(DM.Country.Country, 'A'),
 *   filterFactory.endsWith(DM.Country.Country, 'A'),
 * ])
 * ```
 * @param filters - Filters to intersect. The filters must all be on the same attribute.
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function intersection(filters, config) {
    return new filters_js_1.LogicalAttributeFilter(filters, filters_js_1.LogicalOperators.Intersection, config);
}
exports.intersection = intersection;
/**
 * Creates a filter that excludes items matching the given filter
 * from all items or from items matching the optional input filter.
 *
 * @example
 * Filter for items where the country name does not contain the letter 'A'
 * from the Sample ECommerce data model.
 * ```ts
 * filterFactory.exclude(filterFactory.contains(DM.Country.Country, 'A'))
 * ```
 *
 * Filter for items where the country name starts with the letter 'B' but does not contain the letter 'A'
 * from the Sample ECommerce data model. This filter will match countries like 'Belgium', but will not
 * match countries like 'Bermuda'.
 * ```ts
 * filterFactory.exclude(
 *   filterFactory.contains(DM.Country.Country, 'A'),
 *   filterFactory.startsWith(DM.Country.Country, 'B')
 * )
 * ```
 * @param filter - Filter to exclude
 * @param input - Input filter to exclude from, on the same attribute. If not provided, the filter excludes from all items.
 * @param config - Optional configuration for the filter
 * @returns A filter representing an exclusion of the given filter
 * from all attribute members or from the optional input filter
 */
function exclude(filter, input, config) {
    return new filters_js_1.ExcludeFilter(filter, input, config);
}
exports.exclude = exclude;
// TEXT / NUMERIC FILTERS
/**
 * Creates a filter to isolate attribute values that do not contain a specified string.
 *
 * Matching is case insensitive.
 *
 * You can optionally use wildcard characters for pattern matching, as described in the
 * {@link like | `like()`} function.
 *
 * @example
 * Filter for categories in the Sample ECommerce data model where the category name doesn't contain
 * 'digital'. This filter matches categories not like 'Digital Cameras' and 'MP3 & Digital Media Players'.
 * ```ts
 * filterFactory.contains(DM.Category.Category, 'digital')
 * ```
 * @param attribute - Text attribute to filter on
 * @param value - Value to filter by
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function doesntContain(attribute, value, config) {
    return new filters_js_1.TextFilter(attribute, filters_js_1.TextOperators.DoesntContain, value, config);
}
exports.doesntContain = doesntContain;
/**
 * Creates a filter to isolate attribute values that do not end with a specified string.
 *
 * Matching is case insensitive.
 *
 * You can optionally use wildcard characters for pattern matching, as described in the
 * {@link like | `like()`} function.
 *
 * @example
 * Filter for countries in the Sample ECommerce data model where the country name doesn't end with
 * 'land'. This filter matches countries not like 'Iceland' and 'Ireland'.
 * ```ts
 * filterFactory.doesntEndWith(DM.Country.Country, 'land')
 * ```
 * @param attribute - Text attribute to filter on
 * @param value - Value to filter by
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function doesntEndWith(attribute, value, config) {
    return new filters_js_1.TextFilter(attribute, filters_js_1.TextOperators.DoesntEndWith, value, config);
}
exports.doesntEndWith = doesntEndWith;
/**
 * Creates a filter to isolate attribute values that do not start with a specified string.
 *
 * Matching is case insensitive.
 *
 * You can optionally use wildcard characters for pattern matching, as described in the
 * {@link like | `like()`} function.
 *
 * @example
 * Filter for countries in the Sample ECommerce data model where the country name doesn't start with
 * 'United'. This filter matches countries not like 'United States' and 'United Kingdom'.
 * ```ts
 * filterFactory.doesntStartWith(DM.Country.Country, 'United')
 * ```
 * @param attribute - Text attribute to filter on
 * @param value - Value to filter by
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function doesntStartWith(attribute, value, config) {
    return new filters_js_1.TextFilter(attribute, filters_js_1.TextOperators.DoesntStartWith, value, config);
}
exports.doesntStartWith = doesntStartWith;
/**
 * Creates a filter to isolate attribute values that contain a specified string.
 *
 * Matching is case insensitive.
 *
 * You can optionally use wildcard characters for pattern matching, as described in the
 * {@link like | `like()`} function.
 *
 * @example
 * Filter for categories in the Sample ECommerce data model where the category name contains
 * 'digital'. This filter matches categories like 'Digital Cameras' and 'MP3 & Digital Media Players'.
 * ```ts
 * filterFactory.contains(DM.Category.Category, 'digital')
 * ```
 * @param attribute - Text attribute to filter on
 * @param value - Value to filter by
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function contains(attribute, value, config) {
    return new filters_js_1.TextFilter(attribute, filters_js_1.TextOperators.Contains, value, config);
}
exports.contains = contains;
/**
 * Creates a filter to isolate attribute values that end with a specified string.
 *
 * Matching is case insensitive.
 *
 * You can optionally use wildcard characters for pattern matching, as described in the
 * {@link like | `like()`} function.
 *
 * @example
 * Filter for countries in the Sample ECommerce data model where the country name ends with
 * 'land'. This filter matches countries like 'Ireland' and 'Iceland'.
 * ```ts
 * filterFactory.endsWith(DM.Country.Country, 'land')
 * ```
 * @param attribute - Text attribute to filter on
 * @param value - Value to filter by
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function endsWith(attribute, value, config) {
    return new filters_js_1.TextFilter(attribute, filters_js_1.TextOperators.EndsWith, value, config);
}
exports.endsWith = endsWith;
/**
 * Creates a filter to isolate attribute values that start with a specified string.
 *
 * Matching is case insensitive.
 *
 * You can optionally use wildcard characters for pattern matching, as described in the
 * {@link like | `like()`} function.
 *
 * @example
 * Filter for countries in the Sample ECommerce data model where the country name starts with
 * 'United'. This filter matches countries like 'United States' and 'United Kingdom'.
 * ```ts
 * filterFactory.startsWith(DM.Country.Country, 'United')
 * ```
 * @param attribute - Text attribute to filter on
 * @param value - Value to filter by
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function startsWith(attribute, value, config) {
    return new filters_js_1.TextFilter(attribute, filters_js_1.TextOperators.StartsWith, value, config);
}
exports.startsWith = startsWith;
/**
 * Creates a filter to isolate attribute values that match a specified string pattern.
 *
 * The pattern can include the following wildcard characters:
 *
 * + `_`: Matches a single character
 * + `%`: Matches multiple characters
 *
 * To search for a literal underscore (`_`) or percent symbol (`%`), use the backslash (`\`) escape
 * character.
 *
 * Matching is case insensitive.
 *
 * @example
 * Filter for countries from the Sample ECommerce data model where the country name starts with an
 * 'A' and ends with an 'a'. This filter matches countries like 'Argentina' and 'Australia'.
 * ```ts
 * filterFactory.like(DM.Country.Country, 'A%a')
 * ```
 * @param attribute - Text attribute to filter on
 * @param value - Value to filter by
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function like(attribute, value, config) {
    return new filters_js_1.TextFilter(attribute, filters_js_1.TextOperators.Like, value, config);
}
exports.like = like;
/**
 * Creates a filter to isolate attribute values that do not equal a specified string or number.
 *
 * When filtering against a string:
 *
 *  + Matching is case insensitive.
 *  + You can optionally use wildcard characters for pattern matching, as described in the
 * {@link like | `like()`} function.
 *
 * @example
 * Filter for items not in new condition from the Sample ECommerce data model.
 * ```ts
 * filterFactory.doesntEqual(DM.Commerce.Condition, 'New')
 * ```
 * @param attribute - Text or numeric attribute to filter on
 * @param value - Value to filter by
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function doesntEqual(attribute, value, config) {
    if (typeof value === 'string') {
        return new filters_js_1.TextFilter(attribute, filters_js_1.TextOperators.DoesntEqual, value, config);
    }
    else {
        return numeric(attribute, filters_js_1.NumericOperators.DoesntEqual, value, undefined, undefined, config);
    }
}
exports.doesntEqual = doesntEqual;
/**
 * Creates a filter to isolate attribute values that equal a specified string or number.
 *
 * When filtering against a string:
 *
 *  + Matching is case insensitive.
 *  + You can optionally use wildcard characters for pattern matching, as described in the
 * {@link like | `like()`} function.
 *
 * @example
 * Filter for items in new condition from the Sample ECommerce data model.
 * ```ts
 * filterFactory.equals(DM.Commerce.Condition, 'New')
 * ```
 * @param attribute - Text or numeric attribute to filter on
 * @param value - Value to filter by
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function equals(attribute, value, config) {
    if (typeof value === 'string') {
        return new filters_js_1.TextFilter(attribute, filters_js_1.TextOperators.Equals, value, config);
    }
    else {
        return numeric(attribute, filters_js_1.NumericOperators.Equals, value, undefined, undefined, config);
    }
}
exports.equals = equals;
/**
 * Creates a filter to isolate attribute values strictly greater than a specified number.
 *
 * @example
 * Filter for items where the cost is greater than 100 from the Sample ECommerce data model.
 * ```ts
 * filterFactory.greaterThan(DM.Commerce.Cost, 100)
 * ```
 * @param attribute - Numeric attribute to filter on
 * @param value - Value to filter by
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function greaterThan(attribute, value, config) {
    return numeric(attribute, filters_js_1.NumericOperators.FromNotEqual, value, undefined, undefined, config);
}
exports.greaterThan = greaterThan;
/**
 * Creates a filter to isolate attribute values greater than or equal to a specified number.
 *
 * @example
 * Filter for items where the cost is greater than or equal to 100 from the Sample ECommerce data model.
 * ```ts
 * filterFactory.greaterThanOrEqual(DM.Commerce.Cost, 100)
 * ```
 * @param attribute - Numeric attribute to filter on
 * @param value - Value to filter by
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function greaterThanOrEqual(attribute, value, config) {
    return numeric(attribute, filters_js_1.NumericOperators.From, value, undefined, undefined, config);
}
exports.greaterThanOrEqual = greaterThanOrEqual;
/**
 * Creates a filter to isolate attribute values strictly less than a specified number.
 *
 * @example
 * Filter for items where the cost is less than 100 from the Sample ECommerce data model.
 * ```ts
 * filterFactory.lessThan(DM.Commerce.Cost, 100)
 * ```
 * @param attribute - Numeric attribute to filter on
 * @param value - Value to filter by
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function lessThan(attribute, value, config) {
    return numeric(attribute, filters_js_1.NumericOperators.ToNotEqual, value, undefined, undefined, config);
}
exports.lessThan = lessThan;
/**
 * Creates a filter to isolate attribute values less than or equal to a specified number.
 *
 * @example
 * Filter for items where the cost is less than or equal to 100 from the Sample ECommerce data model.
 * ```ts
 * filterFactory.lessThanOrEqual(DM.Commerce.Cost, 100)
 * ```
 * @param attribute - Numeric attribute to filter on
 * @param value - Value to filter by
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function lessThanOrEqual(attribute, value, config) {
    return numeric(attribute, filters_js_1.NumericOperators.To, value, undefined, undefined, config);
}
exports.lessThanOrEqual = lessThanOrEqual;
/**
 * Creates a filter to isolate attribute values within or exactly matching two specified numerical boundaries.
 *
 * @example
 * Filter for items from the Sample ECommerce data model where the cost is greater than or equal to 100 and less than or equal to 200.
 * ```ts
 * filterFactory.between(DM.Commerce.Cost, 100, 200)
 * ```
 * @param attribute - Numeric attribute to filter on
 * @param valueA - Value to filter from
 * @param valueB - Value to filter to
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function between(attribute, valueA, valueB, config) {
    return numeric(attribute, filters_js_1.NumericOperators.From, valueA, filters_js_1.NumericOperators.To, valueB, config);
}
exports.between = between;
/**
 * Creates a filter that isolates attribute values strictly within two specified numerical boundaries.
 *
 * @example
 * Filter for items from the Sample ECommerce data model where the cost is greater than 100 and less than 200.
 * ```ts
 * filterFactory.betweenNotEqual(DM.Commerce.Cost, 100, 200)
 * ```
 * @param attribute - Numeric attribute to filter on
 * @param valueA - Value to filter from
 * @param valueB - Value to filter to
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function betweenNotEqual(attribute, valueA, valueB, config) {
    return numeric(attribute, filters_js_1.NumericOperators.FromNotEqual, valueA, filters_js_1.NumericOperators.ToNotEqual, valueB, config);
}
exports.betweenNotEqual = betweenNotEqual;
/**
 * Creates a custom numeric filter that filters for given attribute values.
 *
 * @example
 * Filter for items where the cost is greater than 100 and less than 200
 * from the Sample ECommerce data model.
 * ```ts
 * filterFactory.numeric(
 *   DM.Commerce.Cost,
 *   NumericOperators.From,
 *   100,
 *   NumericOperators.To,
 *   200
 * )
 * ```
 * @param attribute - Numeric attribute to filter
 * @param operatorA - First operator
 * @param valueA - First value
 * @param operatorB - Second operator
 * @param valueB - Second value
 * @param config - Optional configuration for the filter
 * @returns A custom numeric filter of the given attribute
 */
function numeric(attribute, operatorA, valueA, operatorB, valueB, config) {
    return new filters_js_1.NumericFilter(attribute, operatorA, valueA, operatorB, valueB, config);
}
exports.numeric = numeric;
/**
 * Creates a filter to isolate attribute values that match any of the specified strings.
 *
 * Matching is case sensitive.
 *
 * @example
 * Filter for items where the condition is 'Used' or 'Refurbished'
 * from the Sample ECommerce data model.
 * ```ts
 * filterFactory.members(DM.Commerce.Condition, ['Used', 'Refurbished'])
 * ```
 * @param attribute - Attribute to filter on
 * @param members - Array of member values to filter by
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 * @shortDescription Creates filter on attribute to match certain string values
 */
function members(attribute, members, config) {
    return new filters_js_1.MembersFilter(attribute, members, config);
}
exports.members = members;
// DATE FILTERS
/**
 * Creates a filter to isolate date values starting from and including the given date and level.
 *
 * @example
 * Filter for items in the Sample ECommerce data model where the date is not before the year 2010.
 * ```ts
 * filterFactory.dateFrom(DM.Commerce.Date.Years, '2010-01')
 * ```
 * @param level - Date {@link LevelAttribute} to filter on
 * @param from - Date or string representing the value to filter from
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function dateFrom(level, from, config) {
    return dateRange(level, from, undefined, config);
}
exports.dateFrom = dateFrom;
/**
 * Creates a filter to isolate items up until and including the given date and level.
 *
 * @example
 * Filter for items where the date is from the year 2010 or earlier in the Sample ECommerce data model.
 * ```ts
 * filterFactory.dateTo(DM.Commerce.Date.Years, '2010-01')
 * ```
 * @param level - Date {@link LevelAttribute} to filter on
 * @param to - Date or string representing the last member to filter to
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function dateTo(level, to, config) {
    return dateRange(level, undefined, to, config);
}
exports.dateTo = dateTo;
/**
 * Creates a filter to isolate items between and including the given dates and level.
 *
 * @example
 * Filter for items in the Sample ECommerce data model where the date is from the years 2009, 2010, or 2011.
 * ```ts
 * filterFactory.dateRange(DM.Commerce.Date.Years, '2009-01', '2011-01')
 * ```
 * @param level - Date {@link LevelAttribute} to filter on
 * @param from - Date or string representing the start member to filter from
 * @param to - Date or string representing the end member to filter to
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function dateRange(level, from, to, config) {
    return new filters_js_1.DateRangeFilter(level, from, to, config);
}
exports.dateRange = dateRange;
/**
 * Creates a filter to isolate items with a date dimension value within a specified range after a
 * given date and level.
 *
 * Although the `offset` can be used to set a beginning date prior to the `anchor`, the filter range always
 * continues forward after the offset beginning date. So, using an `offset` of `-6` and a `count` of `18` when `level`
 * is a month level creates a range that begins 6 month before the `anchor` date and extends to 12 months after
 * the `anchor` date.
 *
 * @example
 * Filter for items in the Sample ECommerce data model where the date is in 2011 or the first half of 2012.
 * ```ts
 * filterFactory.dateRelative(DM.Commerce.Date.Months, 0, 18, '2011-01'),
 * ```
 *
 * Filter for items in the Sample ECommerce data model where the date is in the second half of 2010 or in 2011.
 * ```ts
 * filterFactory.dateRelative(DM.Commerce.Date.Months, -6, 18, '2011-01'),
 * ```
 *
 * Filter for items in the Sample ECommerce data model where the date is in the past 6 months.
 * ```ts
 * filterFactory.dateRelative(DM.Commerce.Date.Months, -6, 6),
 * ```
 * @param level - Date {@link LevelAttribute} to filter on
 * @param offset - Number of levels to skip from the given `anchor` or the default of the current day.
 * Positive numbers skip forwards and negative numbers skip backwards (e.g. `-6` is 6 months backwards when `level` is a months level attribute)
 * @param count - Number of levels to include in the filter (e.g. `6` is 6 months when `level` is a months level attribute)
 * @param anchor - Date to filter from, defaults to the current day
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function dateRelative(level, offset, count, anchor, config) {
    return new filters_js_1.RelativeDateFilter(level, offset, count, undefined, anchor, config);
}
exports.dateRelative = dateRelative;
/**
 * Creates a filter to isolate items with a date dimension value within a specified range after a
 * given date and level.
 *
 * @example
 * Filter for items in the Sample ECommerce data model where the date is in 2011 or the first half of 2012.
 * ```ts
 * filterFactory.dateRelativeFrom(DM.Commerce.Date.Months, 0, 18, '2011-01'),
 * ```
 * @param level - Date {@link LevelAttribute} to filter on
 * @param offset - Number of levels to skip from the given `anchor` or the default of the current day (e.g. `6` is 6 months when `level` is a months level attribute)
 * @param count - Number of levels to include in the filter (e.g. `6` is 6 months when `level` is a months level attribute)
 * @param anchor - Date to filter from, defaults to the current day
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function dateRelativeFrom(level, offset, count, anchor, config) {
    return new filters_js_1.RelativeDateFilter(level, offset, count, filters_js_1.DateOperators.Next, anchor, config);
}
exports.dateRelativeFrom = dateRelativeFrom;
/**
 * Creates a filter to isolate items with a date dimension value within a specified range before a
 * given date and level.
 *
 * @example
 * Filter for items in the Sample ECommerce data model where the date is in the first half of 2010 or in 2011.
 * ```ts
 * filterFactory.dateRelativeTo(DM.Commerce.Date.Months, 0, 18, '2011-12'),
 * ```
 * @param level - Date {@link LevelAttribute} to filter on
 * @param offset - Number of levels to skip from the given `anchor` or the default of the current day (e.g. `6` is 6 months when `level` is a months level attribute)
 * @param count - Number of levels to include in the filter (e.g. `6` is 6 months when `level` is a months level attribute)
 * @param anchor - Date to filter to, defaults to the current day
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function dateRelativeTo(level, offset, count, anchor, config) {
    return new filters_js_1.RelativeDateFilter(level, offset, count, filters_js_1.DateOperators.Last, anchor, config);
}
exports.dateRelativeTo = dateRelativeTo;
/**
 * Creates a filter to isolate items with a date dimension value in the current calendar year.
 *
 * @example
 * Filter for items where the date is in the current calendar year in the Sample ECommerce data model.
 * ```ts
 * filterFactory.thisYear(DM.Commerce.Date)
 * ```
 * @param dimension - Date dimension to filter
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function thisYear(dimension, config) {
    return dateRelativeTo(dimension.Years, 0, 1, undefined, config);
}
exports.thisYear = thisYear;
/**
 * Creates a filter to isolate items with a date dimension value in the current calendar month.
 *
 * @example
 * Filter for items where the date is in the current calendar month in the Sample ECommerce data model.
 * ```ts
 * filterFactory.thisMonth(DM.Commerce.Date)
 * ```
 * @param dimension - Date dimension to filter
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function thisMonth(dimension, config) {
    return dateRelativeTo(dimension.Months, 0, 1, undefined, config);
}
exports.thisMonth = thisMonth;
/**
 * Creates a filter to isolate items with a date dimension value in the current quarter.
 *
 * @example
 * Filter for items where the date is in the current quarter in the Sample ECommerce data model.
 * ```ts
 * filterFactory.thisQuarter(DM.Commerce.Date)
 * ```
 * @param dimension - Date dimension to filter
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function thisQuarter(dimension, config) {
    return dateRelativeTo(dimension.Quarters, 0, 1, undefined, config);
}
exports.thisQuarter = thisQuarter;
/**
 * Creates a filter to isolate items with a date dimension value of the current date.
 *
 * @example
 * Filter for items where the date is today in the Sample ECommerce data model.
 * ```ts
 * filterFactory.today(DM.Commerce.Date)
 * ```
 * @param dimension - date dimension to filter
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function today(dimension, config) {
    return dateRelativeTo(dimension.Days, 0, 1, undefined, config);
}
exports.today = today;
// MEASURE-RELATED FILTERS
/**
 * Creates a filter on all measure values matching the provided criteria.
 *
 * @param attribute - Attribute to filter
 * @param measure - Measure to filter by
 * @param operatorA - Operator to apply on `valueA` ({@link NumericOperators})
 * @param valueA - First value
 * @param operatorB - Operator to apply on `valueB` ({@link NumericOperators})
 * @param valueB - Second value
 * @param config - Optional configuration for the filter
 * @returns A filter representing the provided logic
 * @internal
 */
function measureBase(attribute, measure, operatorA, valueA, operatorB, valueB, config) {
    return new filters_js_1.MeasureFilter(attribute, measure, operatorA, valueA, operatorB, valueB, config);
}
exports.measureBase = measureBase;
/**
 * Creates a filter to isolate a measure value equal to a given number.
 *
 * @example
 * Filter for categories that have an average revenue equal 50 in the Sample ECommerce data model.
 * ```ts
 * filterFactory.measureEquals(
 *   measureFactory.average(DM.Commerce.Revenue),
 *   50
 * )
 * ```
 * @param measure - Measure to filter by
 * @param value - Value
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function measureEquals(measure, value, config) {
    return measureBase(measure.attribute, measure, filters_js_1.NumericOperators.Equals, value, undefined, undefined, config);
}
exports.measureEquals = measureEquals;
/**
 * Creates a filter to isolate a measure value greater than to a given number.
 *
 * @example
 * Filter for categories that have an average revenue greater than
 * to 50 in the Sample ECommerce data model.
 * ```ts
 * filterFactory.measureGreaterThan(
 *   measureFactory.average(DM.Commerce.Revenue),
 *   50
 * )
 * ```
 * @param measure - Measure to filter by
 * @param value - Min value
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function measureGreaterThan(measure, value, config) {
    return measureBase(measure.attribute, measure, filters_js_1.NumericOperators.FromNotEqual, value, undefined, undefined, config);
}
exports.measureGreaterThan = measureGreaterThan;
/**
 * Creates a filter to isolate a measure value greater than or equal to a given number.
 *
 * @example
 * Filter for categories that have an average revenue greater than
 * or equal to 50 in the Sample ECommerce data model.
 * ```ts
 * filterFactory.measureGreaterThanOrEqual(
 *   measureFactory.average(DM.Commerce.Revenue),
 *   50
 * )
 * ```
 * @param measure - Measure to filter by
 * @param value - Min value
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function measureGreaterThanOrEqual(measure, value, config) {
    return measureBase(measure.attribute, measure, filters_js_1.NumericOperators.From, value, undefined, undefined, config);
}
exports.measureGreaterThanOrEqual = measureGreaterThanOrEqual;
/**
 * Creates a filter to isolate a measure value less than or equal to a given number.
 *
 * @example
 * Filter for categories that have an average revenue less than
 * or equal to 100 in the Sample ECommerce data model.
 * ```ts
 * filterFactory.measureLessThanOrEqual(
 *   measureFactory.average(DM.Commerce.Revenue),
 *   100
 * )
 * ```
 * @param measure - Measure to filter by
 * @param value - Max value
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function measureLessThanOrEqual(measure, value, config) {
    return measureBase(measure.attribute, measure, filters_js_1.NumericOperators.To, value, undefined, undefined, config);
}
exports.measureLessThanOrEqual = measureLessThanOrEqual;
/**
 * Creates a filter to isolate a measure value less than a given number.
 *
 * @example
 * Filter for categories that have an average revenue less than 100 in the Sample ECommerce data model.
 * ```ts
 * filterFactory.measureLessThan(
 *   measureFactory.average(DM.Commerce.Revenue),
 *   100
 * )
 * ```
 * @param measure - Measure to filter by
 * @param value - Value
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function measureLessThan(measure, value, config) {
    return measureBase(measure.attribute, measure, filters_js_1.NumericOperators.ToNotEqual, value, undefined, undefined, config);
}
exports.measureLessThan = measureLessThan;
/**
 * Creates a filter to isolate a measure value between or equal to two given numbers.
 *
 * @example
 * Filter for categories that have an average revenue greater than or equal to 50 and less than
 * or equal to 100 in the Sample ECommerce data model.
 * ```ts
 * filterFactory.measureBetween(
 *   measureFactory.average(DM.Commerce.Revenue),
 *   50,
 *   100
 * )
 * ```
 * @param measure - Measure to filter by
 * @param valueA - Min value
 * @param valueB - Max value
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function measureBetween(measure, valueA, valueB, config) {
    return measureBase(measure.attribute, measure, filters_js_1.NumericOperators.From, valueA, filters_js_1.NumericOperators.To, valueB, config);
}
exports.measureBetween = measureBetween;
/**
 * Creates a filter to isolate a measure value between but not equal to two given numbers.
 *
 * @example
 * Filter for categories that have an average revenue greater than 50 and less than
 * 100 in the Sample ECommerce data model.
 * ```ts
 * filterFactory.measureBetweenNotEqual(
 *   measureFactory.average(DM.Commerce.Revenue),
 *   50,
 *   100
 * )
 * ```
 * @param measure - Measure to filter by
 * @param valueA - Min value
 * @param valueB - Max value
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function measureBetweenNotEqual(measure, valueA, valueB, config) {
    return measureBase(measure.attribute, measure, filters_js_1.NumericOperators.FromNotEqual, valueA, filters_js_1.NumericOperators.ToNotEqual, valueB, config);
}
exports.measureBetweenNotEqual = measureBetweenNotEqual;
// RANKING FILTERS
/**
 * Creates a filter to isolate items that rank towards the top for a given measure.
 *
 * @example
 * Filter for age ranges with the top 3 highest total revenue in the Sample ECommerce data model.
 * ```ts
 * filterFactory.topRanking(
 *   DM.Commerce.AgeRange,
 *   measureFactory.sum(DM.Commerce.Revenue),
 *   3
 * )
 * ```
 * @param attribute - Attribute to filter
 * @param measure - Measure to filter by
 * @param count - Number of members to return
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function topRanking(attribute, measure, count, config) {
    return new filters_js_1.RankingFilter(attribute, measure, filters_js_1.RankingOperators.Top, count, config);
}
exports.topRanking = topRanking;
/**
 * Creates a filter to isolate items that rank towards the bottom for a given measure.
 *
 * @example
 * Filter for age ranges with the bottom 3 lowest total revenue in the Sample ECommerce data model.
 * ```ts
 * filterFactory.bottomRanking(
 *   DM.Commerce.AgeRange,
 *   measureFactory.sum(DM.Commerce.Revenue),
 *   3
 * )
 * ```
 * @param attribute - Attribute to filter
 * @param measure - Measure to filter by
 * @param count - Number of members to return
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function bottomRanking(attribute, measure, count, config) {
    return new filters_js_1.RankingFilter(attribute, measure, filters_js_1.RankingOperators.Bottom, count, config);
}
exports.bottomRanking = bottomRanking;
const relate = (node) => {
    if (Array.isArray(node)) {
        const [first, ...rest] = node;
        return rest.length === 0
            ? relate(first)
            : {
                operator: 'AND',
                left: relate(first),
                right: relate(rest),
            };
    }
    return node;
};
// CASCADING FILTERS
/**
 * Creates a filter that contains a list of dependent/cascading filters,
 * where each filter depends on the results or state of the previous ones in the array.
 *
 * Each filter in the array operates in the context of its predecessors, and the
 * cascading behavior ensures that all filters are applied sequentially.
 *
 * @example
 * ```ts
 * // Create a cascading filter for gender and age range
 * const cascadingFilter = filterFactory.cascading(
 *   [
 *     filterFactory.members(DM.Commerce.Gender, ['Male', 'Female']),
 *     filterFactory.members(DM.Commerce.AgeRange, ['0-18']),
 *   ],
 *   { disabled: true }, // Optional configuration to disable the cascading filter
 * );
 * ```
 * @param filters - Array of dependent filters
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 */
function cascading(filters, config) {
    return new filters_js_1.CascadingFilter(filters, config);
}
exports.cascading = cascading;
/**
 * Set of logic operators for filter relations construction
 *
 * These operators are still in beta.
 *
 * @example
 * ```ts
 * import { filters } from '@sisense/sdk-data';
 *
 * // define filters
 * const revenueFilter = filterFactory.greaterThan(DM.Commerce.Revenue, 1000);
 * const countryFilter = filterFactory.members(DM.Commerce.Country, ['USA', 'Canada']);
 * const genderFilter = filterFactory.doesntContain(DM.Commerce.Gender, 'Unspecified');
 * const costFilter = filterFactory.between(DM.Commerce.Cost, 1000, 2000);
 *
 * // create filter relations of two filters
 * const orFilerRelations = filterFactory.logic.or(revenueFilter, countryFilter);
 * // revenueFilter OR countryFilter
 *
 * // filter relations can have nested filter relations
 * const mixedFilterRelations = filterFactory.logic.and(genderFilter, orFilerRelations);
 * // genderFilter AND (revenueFilter OR countryFilter)
 *
 * // array, specified in filter relations, will be converted to an intersection of filters automatically
 * const arrayFilterRelations = filterFactory.logic.or([genderFilter, costFilter], mixedFilterRelations);
 * // (genderFilter AND costFilter) OR (genderFilter AND (revenueFilter OR countryFilter))
 * ```
 * @beta
 */
// eslint-disable-next-line @typescript-eslint/no-namespace
var logic;
(function (logic) {
    /**
     * Creates an 'AND' filter relations
     *
     * @example
     * Create filter relations for items that have a revenue greater than 100 and are in new condition
     * in the Sample ECommerce data model.
     * ```ts
     * const revenueFilter = filterFactory.greaterThan(DM.Commerce.Revenue, 100);
     * const conditionFilter = filterFactory.equals(DM.Commerce.Condition, 'New');
     *
     * const andFilerRelation = filterFactory.logic.and(revenueFilter, conditionFilter);
     * ```
     * @param left First filter or filter relations
     * @param right Second filter or filter relations
     * @returns Filter relations
     * @beta
     */
    logic.and = (left, right) => ({
        operator: 'AND',
        left: relate(left),
        right: relate(right),
    });
    /**
     * Creates an 'OR' filter relations
     *
     * @example
     * Create filter relations for items that have a revenue greater than 100 or are in new condition
     * in the Sample ECommerce data model.
     * ```ts
     * const revenueFilter = filterFactory.greaterThan(DM.Commerce.Revenue, 100);
     * const conditionFilter = filterFactory.equals(DM.Commerce.Condition, 'New');
     *
     * const orFilerRelation = filterFactory.logic.or(revenueFilter, conditionFilter);
     * ```
     * @param left First filter or filter relations
     * @param right Second filter or filter relations
     * @returns Filter relations
     * @beta
     */
    logic.or = (left, right) => ({
        operator: 'OR',
        left: relate(left),
        right: relate(right),
    });
})(logic = exports.logic || (exports.logic = {}));
// CUSTOM FILTER
/**
 * Creates a filter from JAQL
 *
 * @param attribute - Attribute to filter
 * @param jaql - Filter Jaql
 * @param config - Optional configuration for the filter
 * @returns A filter instance
 * @internal
 */
function customFilter(attribute, jaql, config) {
    return new filters_js_1.CustomFilter(attribute, jaql, config);
}
exports.customFilter = customFilter;
